import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sensors } from "../mock"; // solo metadati (id, title, subtitle, icon, info, yRange)
import { useAuth } from "../context/AuthContext";
import { backendTodayToSensors, pointsToChartSeries } from "../lib/sensorApi";
import SensorCard from "../components/SensorCard";
import GoalHomeCard from "../components/GoalHomeCard";
import { BACKEND_URL } from "../lib/config";
import {
  Moon,
  Sparkles,
  BedDouble,
  ChevronRight,
  NotebookPen,
  WifiOff,
  Loader2,
} from "lucide-react";

const DEFAULT_PRODUCT_IMG =
  "https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=900&auto=format&fit=crop";

export default function Home() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [productImg, setProductImg] = useState(DEFAULT_PRODUCT_IMG);
  const [productAlt, setProductAlt] = useState("Materasso Materassi & Materassi");

  const [todayData, setTodayData] = useState(null); // null=loading, {has_data,...}
  const [sensorAggs, setSensorAggs] = useState(null); // mappato per UI o null
  const [heartSeries, setHeartSeries] = useState([]); // per sparkline heart card
  const [loadErr, setLoadErr] = useState("");

  // Branding pubblico
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/config/branding`)
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (b?.home_product_image_url) setProductImg(b.home_product_image_url);
        if (b?.home_product_image_alt) setProductAlt(b.home_product_image_alt);
      })
      .catch(() => {});
  }, []);

  // Dati sonno reali
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [todayRes, hrRes] = await Promise.all([
          api.get("/api/user/me/today"),
          api.get("/api/user/me/today/series?sensor=heart_r&limit=300"),
        ]);
        if (cancelled) return;
        setTodayData(todayRes.data);
        setSensorAggs(backendTodayToSensors(todayRes.data));
        if (hrRes.data?.has_data) {
          setHeartSeries(pointsToChartSeries(hrRes.data.points || []));
        } else {
          setHeartSeries([]);
        }
      } catch (e) {
        if (cancelled) return;
        setLoadErr("Impossibile caricare i dati del sonno");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const isLoading = todayData === null && !loadErr;
  const hasData = !!todayData?.has_data;
  const durationLabel = hasData
    ? `${Math.floor((todayData.duration_min || 0) / 60)}h ${(todayData.duration_min || 0) % 60}m`
    : "—";

  // Per ogni sensor metadata, costruisci l'oggetto "rich" che SensorCard si aspetta
  const sensorsForCards = sensors.map((s) => {
    const agg = sensorAggs?.[s.id];
    return {
      ...s,
      current: agg?.current ?? "—",
      min: agg?.min ?? "—",
      max: agg?.max ?? "—",
      avg: agg?.avg ?? "—",
    };
  });

  return (
    <div className="px-4 pt-5 pb-36">
      {/* Hero card */}
      <section
        className="relative overflow-hidden rounded-[26px] p-5"
        style={{
          background:
            "linear-gradient(150deg, #12b096 0%, #0a8670 45%, #083d37 100%)",
          boxShadow:
            "0 16px 40px -16px rgba(0,168,138,0.55), inset 0 1px 0 rgba(255,255,255,0.15)",
        }}
      >
        <div className="absolute -top-16 -left-10 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-emerald-300/10 blur-3xl pointer-events-none" />

        <div className="flex items-center gap-2 text-white/90 text-xs uppercase tracking-[0.15em] mb-2">
          <Sparkles size={14} /> SmartDream
        </div>
        <h1 className="text-white text-[32px] leading-[1.05] font-semibold tracking-tight">
          Ciao {user?.firstName || user?.name || "👋"}!
        </h1>
        <p className="mt-2 text-white/90 text-[15px] leading-snug max-w-[80%]">
          {hasData
            ? "Ecco il riepilogo della tua ultima notte."
            : "SmartDream è pronto per monitorare il tuo riposo."}
        </p>

        {/* Floating mattress illustration */}
        <div className="relative mt-5 flex items-center justify-center h-[170px]">
          <div className="absolute right-2 top-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="absolute rounded-full border border-white/25"
                style={{
                  width: 80 + i * 40,
                  height: 80 + i * 40,
                  top: -(40 + i * 20),
                  right: -(40 + i * 20),
                  animation: `pulseRing 3s ${i * 0.4}s ease-out infinite`,
                  opacity: 0.5 - i * 0.08,
                }}
              />
            ))}
          </div>

          <img
            src={productImg}
            alt={productAlt}
            data-testid="home-product-image"
            className="w-[82%] max-w-[320px] rounded-lg object-cover drop-shadow-2xl rotate-[-6deg] animate-[float_6s_ease-in-out_infinite]"
            style={{ filter: "drop-shadow(0 20px 25px rgba(0,0,0,0.35))" }}
          />
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="flex-1 h-2 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full bg-white/90 transition-all"
              style={{
                width: hasData
                  ? `${Math.min(100, Math.round(((todayData.duration_min || 0) / 480) * 100))}%`
                  : "8%",
              }}
            />
          </div>
          <div
            className="flex items-center gap-1 text-white/90 text-xs"
            data-testid="home-duration"
          >
            <Moon size={14} /> {durationLabel}
          </div>
        </div>
      </section>

      {/* Goal home card */}
      <GoalHomeCard />

      {/* Bedtime + Diario CTA */}
      <section className="mt-4 grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate("/bedtime")}
          className="rounded-[22px] p-4 text-left transition-transform hover:-translate-y-0.5 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #152439 0%, #0f2b3a 40%, #0a3b3a 100%)",
            boxShadow: "0 16px 40px -20px rgba(20,194,161,0.4)",
            border: "1px solid rgba(103,232,209,0.15)",
          }}
        >
          <div className="absolute -top-8 -right-4 w-24 h-24 rounded-full bg-[#14c2a1]/15 blur-2xl pointer-events-none" />
          <div className="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center mb-2">
            <BedDouble size={18} className="text-[#67e8d1]" />
          </div>
          <div className="text-white text-[15px] font-semibold">Buonanotte</div>
          <div className="text-white/70 text-[11.5px] mt-0.5 leading-snug">
            Avvia il monitoraggio con Sleep Aid
          </div>
        </button>

        <button
          onClick={() => navigate("/diario")}
          className="rounded-[22px] p-4 text-left transition-transform hover:-translate-y-0.5 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #12b096 0%, #0a8670 55%, #083d37 100%)",
            boxShadow: "0 16px 40px -20px rgba(20,194,161,0.5)",
          }}
        >
          <div className="absolute -top-8 -right-4 w-24 h-24 rounded-full bg-white/15 blur-2xl pointer-events-none" />
          <div className="w-10 h-10 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center mb-2">
            <NotebookPen size={18} className="text-white" />
          </div>
          <div className="text-white text-[15px] font-semibold">Diario</div>
          <div className="text-white/85 text-[11.5px] mt-0.5 leading-snug">
            Come hai vissuto oggi?
          </div>
        </button>
      </section>

      {/* Data heading */}
      <section className="mt-6">
        <h2 className="text-white text-[26px] font-semibold tracking-tight leading-tight">
          Dati rilevati
        </h2>
        <p className="text-white/60 text-sm mt-1">Monitoraggio ogni 5 minuti</p>

        {/* Banner informativo (non blocca le sezioni sotto) */}
        {isLoading && (
          <div
            className="mt-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 flex items-center text-white/70 text-[12.5px] gap-2"
            data-testid="home-sensors-loading"
          >
            <Loader2 size={14} className="animate-spin" />
            Caricamento dati…
          </div>
        )}

        {loadErr && !isLoading && (
          <div
            className="mt-3 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-2.5 text-rose-100 text-[12.5px] flex items-start gap-2"
            data-testid="home-sensors-error"
          >
            <WifiOff size={14} className="mt-0.5" />
            <div>{loadErr}. Verifica la connessione.</div>
          </div>
        )}

        {!isLoading && !loadErr && !hasData && (
          <div
            className="mt-3 rounded-2xl border border-[#14c2a1]/25 bg-[#14c2a1]/10 px-4 py-3 text-[12.5px] text-[#67e8d1] flex items-start gap-2"
            data-testid="home-empty-state"
          >
            <Moon size={14} className="mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-white">
                Nessun dato disponibile
              </div>
              <div className="text-white/70 mt-0.5">
                Connetti il tuo SmartDream per iniziare il monitoraggio.
              </div>
            </div>
            <button
              onClick={() => navigate("/bedtime")}
              className="text-[#67e8d1] hover:text-white text-[12px] font-medium flex items-center gap-0.5 whitespace-nowrap"
            >
              Avvia <ChevronRight size={12} />
            </button>
          </div>
        )}

        {/* Sensor grid: SEMPRE visibile, con empty state interno se senza dati */}
        <div className="mt-4 grid grid-cols-2 gap-3" data-testid="home-sensors-grid">
          {sensorsForCards.map((s) => (
            <SensorCard
              key={s.id}
              sensor={s}
              data={s.id === "heartRate" ? heartSeries : []}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
